import type Report from '../models/reports';
import type { SearchConfig } from '../models/reports';
import type { ChartType } from 'tio-common/types/chart';
import type { DateGroupingInterval } from './date-grouping';
import { ReportingConfigManager, type UnifiedReportConfig } from './config-manager';
import type { AggregationSortOrder } from '../components/chart-view';

export interface ReportLoadContext {
  // For chart-view components
  setDataset?: (dataset: string) => void;
  setGroupByField?: (field: string) => void;
  setChartType?: (type: ChartType) => void;
  setTimeRange?: (range: string) => void;
  setDateGrouping?: (grouping: DateGroupingInterval) => void;
  setShowTableView?: (show: boolean) => void;
  setSortOrder?: (order: string) => void;
  clearQueryCache?: () => void;

  // For TAS programs components
  updateQueryParameters?: (params: Record<string, unknown>) => void;

  // Common
  setCurrentReport?: (report: Report) => void;
}

export interface LoadResult {
  success: boolean;
  config?: UnifiedReportConfig;
  error?: string;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  if (typeof error === 'string' && error.trim().length > 0) {
    return error;
  }

  return 'Unknown error';
}

function buildErrorLogPayload(error: unknown): unknown {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
      cause: error.cause,
    };
  }

  return error;
}

function stringifyErrorForLog(error: unknown): string {
  const payload = buildErrorLogPayload(error);

  if (typeof payload === 'string') {
    return payload;
  }

  try {
    return JSON.stringify(payload, null, 2);
  } catch {
    return String(payload);
  }
}

/**
 * Report loader that can handle both chart and table report types
 * Preserves existing chart bucketing logic and handles TAS program filters
 */
export class ReportLoader {
  /**
   * Load a saved report into the appropriate component context
   * Intelligently detects report type and applies the correct loading strategy
   */
  static async loadReport(report: Report, context: ReportLoadContext): Promise<LoadResult> {
    try {
      const searchConfig = report.searchConfig as SearchConfig;

      // Determine report type from the report itself or infer from content
      const reportType = report.reportType || this.inferReportType(searchConfig);

      switch (reportType) {
        case 'chart_builder':
          return this.loadChartReport(report, searchConfig, context);

        case 'table_builder':
          return this.loadTableReport(report, searchConfig, context);

        case 'tas_programs_unified':
          return this.loadUnifiedReport(report, searchConfig, context);

        default:
          // Try to auto-detect and load appropriately
          return this.loadAutoDetected(report, searchConfig, context);
      }
    } catch (error) {
      const message = extractErrorMessage(error);
      console.error('Error loading report:', stringifyErrorForLog(error));
      return {
        success: false,
        error: `Failed to load report: ${message}`,
      };
    }
  }

  /**
   * Load chart-specific report (preserves existing chart bucketing logic)
   */
  private static loadChartReport(
    report: Report,
    searchConfig: SearchConfig,
    context: ReportLoadContext
  ): LoadResult {
    // Check for new format with chartSettings first
    if (searchConfig.chartSettings) {
      // New format: use chartSettings for chart-specific properties
      const chartSettings = searchConfig.chartSettings as Record<string, unknown>;

      // Apply chart settings using conditional calls
      if (chartSettings.selectedDataset && context.setDataset) {
        context.setDataset(chartSettings.selectedDataset as string);
      }
      // Support both selectedGroupByField (new) and selectedField (legacy/seed format)
      const groupByField = chartSettings.selectedGroupByField || chartSettings.selectedField;
      if (groupByField && context.setGroupByField) {
        context.setGroupByField(groupByField as string);
      }
      if (chartSettings.selectedType && context.setChartType) {
        context.setChartType(chartSettings.selectedType as ChartType);
      }
      if (chartSettings.selectedTimeRange && context.setTimeRange) {
        context.setTimeRange(chartSettings.selectedTimeRange as string);
      }
      if (chartSettings.selectedDateGrouping && context.setDateGrouping) {
        context.setDateGrouping(chartSettings.selectedDateGrouping as DateGroupingInterval);
      }
      if (chartSettings.sortOrder && context.setSortOrder) {
        context.setSortOrder(chartSettings.sortOrder as AggregationSortOrder);
      }
    }
    // Check for metadata format (legacy but still supported)
    else if (searchConfig.metadata) {
      // Metadata format: use metadata for UI state
      const metadata = searchConfig.metadata;

      // Apply chart settings using conditional calls
      if (metadata.dataset && context.setDataset) {
        context.setDataset(metadata.dataset);
      }
      if (metadata.groupByField && context.setGroupByField) {
        context.setGroupByField(metadata.groupByField);
      }
      if (metadata.chartType && context.setChartType) {
        context.setChartType(metadata.chartType as ChartType);
      }
      if (metadata.timeRange && context.setTimeRange) {
        context.setTimeRange(metadata.timeRange);
      }
      if (metadata.dateGrouping && context.setDateGrouping) {
        context.setDateGrouping(metadata.dateGrouping as DateGroupingInterval);
      }
      if (metadata.sortOrder && context.setSortOrder) {
        context.setSortOrder(metadata.sortOrder as AggregationSortOrder);
      }
    } else {
      // Legacy format: fall back to old structure for backward compatibility
      if (searchConfig.selectedDataset && context.setDataset) {
        context.setDataset(searchConfig.selectedDataset);
      }
      if (searchConfig.selectedGroupByField && context.setGroupByField) {
        context.setGroupByField(searchConfig.selectedGroupByField);
      }
      if (searchConfig.selectedChartType && context.setChartType) {
        context.setChartType(searchConfig.selectedChartType as ChartType);
      }
      if (searchConfig.selectedTimeRange && context.setTimeRange) {
        context.setTimeRange(searchConfig.selectedTimeRange);
      }
      if (searchConfig.selectedDateGrouping && context.setDateGrouping) {
        context.setDateGrouping(searchConfig.selectedDateGrouping as DateGroupingInterval);
      }
    }

    // Clear query cache to force refetch with new configuration
    context.clearQueryCache?.();

    // Set as current saved report
    context.setCurrentReport?.(report);

    return { success: true };
  }

  /**
   * Load table-specific report (TAS programs filters)
   */
  private static loadTableReport(
    report: Report,
    searchConfig: SearchConfig,
    context: ReportLoadContext
  ): LoadResult {
    // Convert saved config to query parameters for TAS programs component
    if (context.updateQueryParameters && searchConfig.filters) {
      const filters = searchConfig.filters as Record<string, unknown>;
      const tableSettings = searchConfig.tableSettings as Record<string, unknown> | undefined;

      // Build params using conditional spread pattern (consistent with codebase)
      const params: Record<string, unknown> = {
        // Program filters
        ...(filters.programTemplateId !== undefined && {
          programTemplateId: filters.programTemplateId,
        }),
        ...(filters.programCreatedAtStart !== undefined && {
          programCreatedAtStart: filters.programCreatedAtStart,
        }),
        ...(filters.programCreatedAtEnd !== undefined && {
          programCreatedAtEnd: filters.programCreatedAtEnd,
        }),
        ...(filters.excludeNonVoucherPrograms !== undefined && {
          excludeNonVoucherPrograms: filters.excludeNonVoucherPrograms,
        }),
        ...(filters.courseCashProgramsOnly !== undefined && {
          courseCashProgramsOnly: filters.courseCashProgramsOnly,
        }),

        // Application filters
        ...(filters.applicationStates !== undefined && {
          applicationStates: Array.isArray(filters.applicationStates)
            ? (filters.applicationStates as string[]).join(',')
            : filters.applicationStates,
        }),
        ...(filters.applicationCreatedAtStart !== undefined && {
          applicationCreatedAtStart: filters.applicationCreatedAtStart,
        }),
        ...(filters.applicationCreatedAtEnd !== undefined && {
          applicationCreatedAtEnd: filters.applicationCreatedAtEnd,
        }),

        // Payment filters
        ...(filters.includePayments !== undefined && { includePayments: filters.includePayments }),
        ...(filters.paymentStates !== undefined && {
          paymentStates: Array.isArray(filters.paymentStates)
            ? (filters.paymentStates as string[]).join(',')
            : filters.paymentStates,
        }),
        ...(filters.paymentYear !== undefined && { paymentYear: filters.paymentYear }),

        // Course filters
        ...(filters.includeCourses !== undefined && { includeCourses: filters.includeCourses }),
        ...(filters.coursesBeginDate !== undefined && {
          coursesBeginDate: filters.coursesBeginDate,
        }),
        ...(filters.coursesEndDate !== undefined && { coursesEndDate: filters.coursesEndDate }),

        // Employee filters
        ...(filters.employeeId !== undefined && { employeeId: filters.employeeId }),
        ...(filters.terminatedEmployees !== undefined && {
          terminatedEmployees: filters.terminatedEmployees,
        }),
        ...(filters.excludeTermVerified !== undefined && {
          excludeTermVerified: filters.excludeTermVerified,
        }),
        ...(filters.approverId !== undefined && { approverId: filters.approverId }),

        // Table settings
        ...(tableSettings?.showIndividualApplications !== undefined && {
          showIndividualApplications: tableSettings.showIndividualApplications,
        }),
        // Preserve column order from saved report
        ...(tableSettings?.columnOrder !== undefined && {
          columnOrder: tableSettings.columnOrder,
        }),
      };

      context.updateQueryParameters(params);
    }

    // Set as current saved report
    context.setCurrentReport?.(report);

    return { success: true };
  }

  /**
   * Load unified report that supports both chart and table views
   */
  private static loadUnifiedReport(
    report: Report,
    searchConfig: SearchConfig,
    context: ReportLoadContext
  ): LoadResult {
    try {
      // Try to convert to unified config format
      const urlParams = typeof searchConfig.urlParams === 'string' ? searchConfig.urlParams : '';
      const unifiedConfig = ReportingConfigManager.fromUrlParams(new URLSearchParams(urlParams));

      // Apply based on view type
      if (unifiedConfig.viewType === 'chart' && unifiedConfig.chartSettings) {
        return this.loadChartReport(
          report,
          {
            metadata: {
              dataset: 'tas-program-instances', // Default for unified
              groupByField: unifiedConfig.chartSettings.groupByField,
              chartType: unifiedConfig.chartSettings.chartType,
              dateGrouping: unifiedConfig.chartSettings.dateGrouping,
            },
          },
          context
        );
      } else if (unifiedConfig.viewType === 'table') {
        return this.loadTableReport(
          report,
          {
            filters: unifiedConfig.filters,
            tableSettings: unifiedConfig.tableSettings,
          },
          context
        );
      }

      return { success: false, error: 'Unknown unified report view type' };
    } catch (error) {
      console.error('Error loading unified report:', error);
      return { success: false, error: 'Failed to parse unified report configuration' };
    }
  }

  /**
   * Auto-detect report type and load appropriately
   */
  private static loadAutoDetected(
    report: Report,
    searchConfig: SearchConfig,
    context: ReportLoadContext
  ): LoadResult {
    const reportType = this.inferReportType(searchConfig);

    if (reportType === 'chart_builder') {
      return this.loadChartReport(report, searchConfig, context);
    } else {
      return this.loadTableReport(report, searchConfig, context);
    }
  }

  /**
   * Infer report type from search configuration structure
   */
  private static inferReportType(searchConfig: SearchConfig): string {
    // Check for chart-specific properties
    if (
      searchConfig.metadata?.chartType ||
      searchConfig.selectedChartType ||
      searchConfig.metadata?.groupByField ||
      searchConfig.selectedGroupByField
    ) {
      return 'chart_builder';
    }

    // Check for table-specific properties
    if (searchConfig.filters || searchConfig.tableSettings) {
      return 'table_builder';
    }

    // Check for unified format
    if (searchConfig.urlParams) {
      const urlParams = typeof searchConfig.urlParams === 'string' ? searchConfig.urlParams : '';
      const params = new URLSearchParams(urlParams);
      if (params.has('viewType')) {
        return 'tas_programs_unified';
      }
    }

    // Default to table for backwards compatibility
    return 'table_builder';
  }

  /**
   * Create a unified search config from current state
   * This helps with saving reports in a consistent format
   */
  static createSearchConfig(context: {
    // Chart state
    dataset?: string;
    groupByField?: string;
    chartType?: ChartType;
    timeRange?: string;
    dateGrouping?: DateGroupingInterval;
    showTableView?: boolean;

    // Table state
    queryParameters?: Record<string, unknown>;

    // View state
    viewType?: 'chart' | 'table';
  }): SearchConfig {
    if (context.viewType === 'chart') {
      return {
        metadata: {
          dataset: context.dataset || 'tas-program-instances',
          groupByField: context.groupByField || 'application_state',
          chartType: context.chartType || 'bar',
          timeRange: context.timeRange || 'all',
          dateGrouping: context.dateGrouping || 'day',
        },
      };
    } else {
      // Table format - use filter and Object.entries pattern like rest of codebase
      const filters: Record<string, unknown> = {};
      const params = context.queryParameters || {};

      // Convert query parameters to filter format, filtering out empty values
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (key.includes('States') && typeof value === 'string') {
            filters[key] = value.split(',').filter(Boolean);
          } else {
            filters[key] = value;
          }
        }
      });

      return {
        filters,
        tableSettings: {
          showIndividualApplications: (params.showIndividualApplications as boolean) || false,
        },
      };
    }
  }
}
